import type { SharedStatePayload } from "replo-runtime/shared/types";

import * as React from "react";

import { selectSharedState, setSharedState } from "@v1/reducers/paint-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";

/**
 * Hook for generating paint shared state. This bridges state between
 * the editor and the runtime. It should be used by runtime components
 * anytime they are in need of internal state that can persist
 * in between mounts. This is particularly necessary for us because
 * anytime a component is reparented (like when moving it on the editor),
 * React will remount no matter what.
 */
export default function usePaintSharedState() {
  const sharedState = useEditorSelector(selectSharedState);
  const dispatch = useEditorDispatch();

  return {
    sharedState,
    setSharedState: React.useCallback(
      (setSharedStatePayload: SharedStatePayload) => {
        return dispatch(setSharedState(setSharedStatePayload));
      },
      [dispatch],
    ),
  };
}

export function usePaintSharedStateValue(key: string) {
  const { sharedState, setSharedState } = usePaintSharedState();

  return [
    sharedState[key],
    React.useCallback(
      (value: unknown) => setSharedState({ key, value }),
      [key, setSharedState],
    ),
  ] as const;
}
